import { BackendError, backendFetch } from "@/lib/backend";
import type {
  DocumentBatchUploadResponse,
  DocumentResponse,
  SignedURLResponse,
} from "@/types/backend";

export const PROJECT_DOCUMENT_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  ".docm",
  ".rtf",
  ".ppt",
  ".pptx",
  ".pptm",
  ".pps",
  ".ppsx",
  ".ppsm",
  ".pot",
  ".potx",
  ".potm",
  ".xlsx",
  ".xlsm",
  ".xls",
  ".ods",
  ".csv",
  ".zip",
  ".zipx",
  ".7z",
  ".rar",
  ".tar",
  ".tar.gz",
  ".tgz",
  ".tar.bz2",
  ".tbz2",
  ".tar.xz",
  ".txz",
].join(",");

async function readBodyTextSafe(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

export async function uploadProjectDocuments(
  projectId: string,
  files: readonly File[],
): Promise<DocumentBatchUploadResponse> {
  if (files.length === 0) {
    throw new Error("At least one document is required.");
  }

  const form = new FormData();
  files.forEach((file) => form.append("files", file));

  const response = await backendFetch(`/api/projects/${projectId}/documents`, {
    method: "POST",
    body: form,
  });

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to upload document.", {
      status: response.status,
      bodyText,
    });
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to upload document (unexpected response).", {
      status: response.status,
      bodyText,
    });
  }

  return (await response.json()) as DocumentBatchUploadResponse;
}

export function documentBatchUploadFeedback(
  result: DocumentBatchUploadResponse,
): string {
  const parts = [`${result.documents.length} created`];
  if (result.skipped.length > 0) {
    parts.push(`${result.skipped.length} skipped`);
  }
  if (result.failed.length > 0) {
    parts.push(`${result.failed.length} failed`);
  }
  return parts.join(" · ");
}

export async function listProjectDocuments(
  projectId: string
): Promise<DocumentResponse[]> {
  const response = await backendFetch(`/api/projects/${projectId}/documents`, {
    method: "GET",
  });

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to list documents.", {
      status: response.status,
      bodyText,
    });
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to list documents (unexpected response).", {
      status: response.status,
      bodyText,
    });
  }

  return (await response.json()) as DocumentResponse[];
}

export async function getDocumentDownloadUrl(
  projectId: string,
  documentId: string
): Promise<SignedURLResponse> {
  const response = await backendFetch(
    `/api/projects/${projectId}/documents/${documentId}/download`,
    { method: "GET" }
  );

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to get download URL.", {
      status: response.status,
      bodyText,
    });
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to get download URL (unexpected response).", {
      status: response.status,
      bodyText,
    });
  }

  return (await response.json()) as SignedURLResponse;
}

export async function deleteProjectDocument(
  projectId: string,
  documentId: string
): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${projectId}/documents/${documentId}`,
    { method: "DELETE" }
  );

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to delete document.", {
      status: response.status,
      bodyText,
    });
  }
}
